import { Description } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  MenuItem
} from "@mui/material";
import { COLORS, primaryContainedButtonSx } from "assets";
import { IncidentApiError, incidentsApi } from "incidents/api";
import { alertDocumentationAction } from "incidents/alertConfirm";
import { lookupIncidentForAlert, useIncidentDocs, incidentDocsOn } from "incidents/feature";
import { Incident, IncidentType } from "incidents/types";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ModelTypes, ThreatModel } from "types";
import { getCameraLabel } from "utils";
import { primaryButtonSx, useActor, usePhrase } from "./common";

function incidentType(model?: ModelTypes): IncidentType {
  if (model === "person") return "fall";
  if (model === "abusive") return "unsafe_interaction";
  if (model === "fight") return "responsive_behaviour";
  if (model === "zone") return "wandering_zone";
  return "other";
}

function subtype(model?: ModelTypes): string {
  if (model === "person") return "Fall detected";
  if (model === "abusive") return "Possible pushing, hitting, or grabbing";
  if (model === "fight") return "Responsive behaviour";
  if (model === "zone") return "Zone exit";
  if (model === "gesture") return "Distress recognition";
  if (model === "weapon") return "Weapon alert";
  return "Alert";
}

export function seedFromThreat(threat: ThreatModel) {
  const site = threat.box?.site;
  return {
    alert_id: threat._id,
    facility_id: threat.box?.siteId?.toString() || site?._id?.toString() || "unknown-home",
    facility_name: site?.name || "This home",
    type: incidentType(threat.threatType?.model),
    alert_at: threat.timestamp,
    location: getCameraLabel(threat.camera),
    camera_id: threat.camera?.name || String(threat.cameraId || ""),
    alert_subtype: subtype(threat.threatType?.model),
    province: "other" as const
  };
}

export function IncidentAlertLink({
  threat,
  variant,
  onDone
}: {
  threat: ThreatModel;
  variant: "button" | "menu";
  onDone?: () => void;
}) {
  const phrase = usePhrase();
  const actor = useActor();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const enabled = incidentDocsOn(useIncidentDocs());
  const [incident, setIncident] = useState<Incident | null>(null);
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const action = alertDocumentationAction({
    enabled,
    threatStatus: threat.status,
    incident
  });

  useEffect(() => {
    let cancelled = false;
    lookupIncidentForAlert(enabled, threat._id, (query) => incidentsApi.list(query))
      .then((row) => {
        if (!cancelled) setIncident(row);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [enabled, threat._id]);

  if (action === "hidden") return null;

  const label =
    action === "view"
      ? phrase("View incident", "Voir l'incident")
      : action === "reconfirm"
        ? phrase("Closed alert — confirm to document", "Alerte fermée — confirmer pour documenter")
        : phrase("I opened this alert — start documentation", "J'ai ouvert cette alerte — commencer la documentation");

  async function start(reconfirm: boolean) {
    setBusy(true);
    try {
      const saved = await incidentsApi.confirmFromAlert(threat._id, {
        reconfirm,
        threatStatus: threat.status,
        seed: seedFromThreat(threat),
        actor
      });
      setAsk(false);
      onDone?.();
      navigate(`/incidents/${saved.id}`);
    } catch (err) {
      if (
        err instanceof IncidentApiError &&
        err.status === 409 &&
        !reconfirm &&
        /closed as not an incident/i.test(err.message)
      ) {
        setAsk(true);
      } else {
        enqueueSnackbar(
          err instanceof Error
            ? err.message
            : phrase("Could not start documentation", "Impossible de commencer la documentation"),
          { variant: "error" }
        );
      }
    } finally {
      setBusy(false);
    }
  }

  function onPress() {
    if (action === "view" && incident) {
      onDone?.();
      navigate(`/incidents/${incident.id}`);
      return;
    }
    if (action === "reconfirm") {
      setAsk(true);
      return;
    }
    start(false).catch(() => undefined);
  }

  const control =
    variant === "menu" ? (
      <MenuItem onClick={onPress} disabled={busy} sx={{ minHeight: 44 }}>
        <ListItemIcon sx={{ minWidth: 32, color: COLORS.surface.secondaryText }}>
          <Description fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={label} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
      </MenuItem>
    ) : (
      <Button
        variant="contained"
        onClick={onPress}
        disabled={busy}
        sx={{ ...primaryButtonSx, ...primaryContainedButtonSx }}
        aria-label={label}
      >
        {label}
      </Button>
    );

  return (
    <>
      {control}
      <Dialog open={ask} onClose={() => setAsk(false)} aria-labelledby="reconfirm-alert-title">
        <DialogTitle id="reconfirm-alert-title">
          {phrase("This alert was closed", "Cette alerte a été fermée")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {phrase(
              "It was marked Resolved, or dismissed as not an incident. Documentation starts only if you confirm the alert is real.",
              "Elle a été marquée Résolue, ou rejetée comme n'étant pas un incident. La documentation commence seulement si vous confirmez que l'alerte est réelle."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAsk(false)} disabled={busy}>
            {phrase("Cancel", "Annuler")}
          </Button>
          <Button variant="contained" onClick={() => start(true).catch(() => undefined)} disabled={busy}>
            {phrase("Confirm it's real", "Confirmer que c'est réel")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
